window.cartDrawerCompleteLookState = window.cartDrawerCompleteLookState || {
    get(root = document) {
        const details = root.querySelector?.('.cart-drawer-complete-look__details');
        return details ? details.open : null;
    },
    apply(root = document, state) {
        if (state === null || typeof state === 'undefined') return;

        const details = root.querySelector?.('.cart-drawer-complete-look__details');
        if (details) details.open = Boolean(state);
    },
};

class CartDrawer extends HTMLElement {
    constructor() {
        super();

        this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.setHeaderCartIconAccessibility();
    }

    setHeaderCartIconAccessibility() {
        const cartLinks = document.querySelectorAll('#cart-icon-bubble, [data-cart-drawer-trigger]');
        if (!cartLinks.length) return;

        cartLinks.forEach((cartLink) => {
            if (cartLink.dataset.cartDrawerBound === 'true') return;

            cartLink.dataset.cartDrawerBound = 'true';
            cartLink.setAttribute('role', 'button');
            cartLink.setAttribute('aria-haspopup', 'dialog');
            cartLink.addEventListener('click', (event) => {
                event.preventDefault();
                this.open(cartLink);
            });
            cartLink.addEventListener('keydown', (event) => {
                if (event.code.toUpperCase() === 'SPACE') {
                    event.preventDefault();
                    this.open(cartLink);
                }
            });
        });
    }

    open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
        // here the animation doesn't seem to always get triggered. A timeout seem to help
        setTimeout(() => {
            this.classList.add('animate', 'active');
        });

        this.addEventListener(
            'transitionend',
            () => {
                const containerToTrapFocusOn = this.classList.contains('is-empty')
                    ? this.querySelector('.drawer__inner-empty')
                    : document.getElementById('CartDrawer');
                const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
                trapFocus(containerToTrapFocusOn, focusElement);
            },
            { once: true }
        );

        document.body.classList.add('overflow-hidden');
    }

    close() {
        this.classList.remove('active');
        removeTrapFocus(this.activeElement);
        document.body.classList.remove('overflow-hidden');
    }

    setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute('role', 'button');
        cartDrawerNote.setAttribute('aria-expanded', 'false');

        if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
            cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
        }

        cartDrawerNote.addEventListener('click', (event) => {
            event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
        });

        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState, options = {}) {
        const completeLookOpenState = options.openCompleteLook
            ? true
            : window.cartDrawerCompleteLookState.get();

        this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
        this.productId = parsedState.id;
        this.getSectionsToRender().forEach((section) => {
            const sectionMarkup = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);

            if (section.targetSelector) {
                document.querySelectorAll(section.targetSelector).forEach((element) => {
                    element.innerHTML = sectionMarkup;
                });
                return;
            }

            const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);

            if (!sectionElement) return;
            sectionElement.innerHTML = sectionMarkup;
        });

        window.cartDrawerCompleteLookState.apply(document, completeLookOpenState);

        setTimeout(() => {
            this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
            this.open();
        });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
        return [
            {
                id: 'cart-drawer',
                selector: '#CartDrawer',
            },
            {
                id: 'cart-icon-bubble',
                targetSelector: '[data-cart-icon-bubble], #cart-icon-bubble',
            },
        ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
        this.activeElement = element;
    }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerRecommendationForm extends HTMLElement {
    constructor() {
        super();
        this.form = this.querySelector('form');
        this.button = this.querySelector('button[type="submit"]');
        this.loader = this.querySelector('[data-cart-drawer-recommendation-loader]');
        this.error = this.querySelector('[data-cart-drawer-recommendation-error]');

        this.form?.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
        event.preventDefault();

        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) {
            this.form.submit();
            return;
        }

        if (this.error) this.error.textContent = '';
        this.loader?.classList?.remove('d-none');
        this.button?.setAttribute('disabled', true);
        this.button?.classList?.add('custom-loading');
        this.classList.add('is-loading');

        const formData = new FormData(this.form);
        const body = {
            id: formData.get('id'),
            quantity: Number(formData.get('quantity') || 1),
            sections: cartDrawer.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
        };

        try {
            const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(body),
            });
            const parsedState = await response.json();

            if (!response.ok) {
                throw new Error(parsedState.description || window.cartStrings?.error || 'Could not add this item.');
            }

            cartDrawer.renderContents(parsedState);
            cartDrawer.classList.remove('is-empty');
        } catch (error) {
            if (this.error) this.error.textContent = error.message || window.cartStrings?.error || 'Could not add this item.';
            this.loader?.classList?.add('d-none');
            this.button?.removeAttribute('disabled');
            this.button?.classList?.remove('custom-loading');
            this.classList.remove('is-loading');
        }
    }
}

customElements.define('cart-drawer-recommendation-form', CartDrawerRecommendationForm);

class CartDrawerCompleteLook extends HTMLElement {
    constructor() {
        super();
        this.startX = null;
        this.startY = null;
        this.handleTouchStart = this.onTouchStart.bind(this);
        this.handleTouchEnd = this.onTouchEnd.bind(this);
    }

    connectedCallback() {
        this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        this.addEventListener('touchend', this.handleTouchEnd);
    }

    disconnectedCallback() {
        this.removeEventListener('touchstart', this.handleTouchStart);
        this.removeEventListener('touchend', this.handleTouchEnd);
    }

    onTouchStart(event) {
        if (!this.isMobileViewport() || !event.touches.length) return;

        this.startX = event.touches[0].clientX;
        this.startY = event.touches[0].clientY;
    }

    onTouchEnd(event) {
        if (!this.isMobileViewport() || this.startX === null || this.startY === null || !event.changedTouches.length) return;

        const deltaX = event.changedTouches[0].clientX - this.startX;
        const deltaY = event.changedTouches[0].clientY - this.startY;
        this.startX = null;
        this.startY = null;

        if (deltaY <= 48 || Math.abs(deltaX) > 44) return;

        const details = this.querySelector('.cart-drawer-complete-look__details');
        if (details?.open) {
            details.open = false;
        }
    }

    isMobileViewport() {
        return window.matchMedia?.('(max-width: 749px)').matches;
    }
}

customElements.define('cart-drawer-complete-look', CartDrawerCompleteLook);

document.addEventListener('shopify:section:load', () => {
    document.querySelector('cart-drawer')?.setHeaderCartIconAccessibility();
});

class CartDrawerItems extends CartItems {
    getSectionsToRender() {
        return [
            {
                id: 'CartDrawer',
                section: 'cart-drawer',
                selector: '.drawer__inner',
            },
            {
                id: 'cart-icon-bubble',
                section: 'cart-icon-bubble',
                selector: '.shopify-section',
                targetSelector: '[data-cart-icon-bubble], #cart-icon-bubble',
            },
        ];
    }
}

customElements.define('cart-drawer-items', CartDrawerItems);
